'use client'
import TitleBar from '../layout/TitleBar';
import { JSX, useEffect, useState, useMemo } from 'react';
import { useLocationDetails } from '@/hooks/useLocation';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocationReadings, useGoaEvents } from '@/hooks/useLocation';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Typography, List, ListItem, ListItemText, Button, Box, Grid } from '@mui/material';
import { IGoa } from "@/stack-shared/interfaces/model/goa.interface";
import { GOA_EVENT_ENTRY_TYPE, IGoaEvent } from '@/stack-shared/interfaces/model/goa-event.interface';
import { useOutages } from '@/hooks/api/useOutages';
import { useTodayLocationVisit } from '@/hooks/api/useLocationVisits';
import LocationVisitOverview from '../site-visit/LocationVisitOverview';
import { last, orderBy } from 'lodash';
import LocationVisitGoaForm, { IGoaValue } from '../site-visit/LocationVisitGoaForm';
import LocationVisitTrkForm, { ITrkValue } from '../site-visit/LocationVisitTrkForm';
import { ITrk } from '@/stack-shared/interfaces/model/trk.interface';
import { ITrkEvent } from '@/stack-shared/interfaces/model/trk-event.interface';
import LocationVisitAtmForm, { IAtmValue } from '../site-visit/LocationVisitAtmForm';
import { IAtm } from '@/stack-shared/interfaces/model/atm.interface';
import { IAtmEvent } from '@/stack-shared/interfaces/model/atm-event.interface';
import OutageTable from '@/components/outage/OutageTable';
import LocationRejectsTable from '../reject/LocationRejectsTable';
import { LastValueListItemText } from '../shared/LastValueListItemText';
import { LOCATION_VISIT_STATUS } from '@/stack-shared/interfaces/model/location-visit.interface';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { orange, grey } from "@mui/material/colors";
import ViewListIcon from '@mui/icons-material/ViewList';
import IconButton from '@mui/material/IconButton';
import StandardModal from '../modals/StandardModal';

const DEFAULT_STATUS = LOCATION_VISIT_STATUS.NOT_STARTED;

const getStatusIcon = (status: LOCATION_VISIT_STATUS | null | undefined) => {
  const resolvedStatus = status ?? DEFAULT_STATUS;

  switch (resolvedStatus) {
    case LOCATION_VISIT_STATUS.COMPLETED:
      return <CheckCircleIcon sx={{ color: 'primary.main' }} />;
    case LOCATION_VISIT_STATUS.PENDING:
      return <HourglassEmptyIcon sx={{ color: orange[500] }} />;
    case LOCATION_VISIT_STATUS.NOT_STARTED:
    default:
      return <ScheduleIcon sx={{ color: grey[500] }} />;
  }
};

function LocationServicePage() {

  const { locationId } = useParams();
  const navigate = useNavigate();
  const { data: locationDetails } = useLocationDetails(locationId || '');
  const location = locationDetails?.location;
  const today = new Date();
  const { data: outagesData } = useOutages();
  const { data: todayVisitData } = useTodayLocationVisit();

  const [dynamicData, setDynamicData] = useState<{
    goas: any[], trks: any[], atms: any[], ctms: any[],
    goaEvents: IGoaEvent[], trkEvents: ITrkEvent[], atmEvents: IAtmEvent[], ctmEvents: any[]
  } | null>(null);
  const [openGoaModal, setOpenGoaModal] = useState(false);

  const [openTrkModal, setOpenTrkModal] = useState(false);
  const [openAtmModal, setOpenAtmModal] = useState(false);
  const [openCtmModal, setOpenCtmModal] = useState(false);

  const [trkValues, setTrkValues] = useState<ITrkValue[]>([]);
  const [goaValues, setGoaValues] = useState<IGoaValue[]>([]);
  const [atmValues, setAtmValues] = useState<IAtmValue[]>([]);
  const [ctmValues, setCtmValues] = useState<IGoaValue[]>([]);
  
  const outages = useMemo(() => {
    if (!outagesData) return [];
    return Array.isArray(outagesData) ? outagesData : Object.values(outagesData);
  }, [outagesData]);

  const todayVisit = todayVisitData && locationId && Number(todayVisitData.locationId) === Number(locationId) ? todayVisitData : null;

  console.log("todayVisit full object:", todayVisit);
  console.log("todayVisit statuses:", {
    goa: todayVisit?.goaStatus,
    trk: todayVisit?.trkStatus,
    ctm: todayVisit?.ctmStatus,
    atm: todayVisit?.atmStatus
  });

  const [breadcrumbs, setBreadcrumb] = useState([
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Service', href: '/app/location-service' },
    { label: location?.name || '' }, // No href for the current page
  ]);

  const queryClient = useQueryClient();

  // Get the service date from todayVisit, fallback to today's date
  // Normalize to YYYY-MM-DD format, using local date to match user's timezone
  const normalizeToLocalDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const serviceDateStr = todayVisit?.date 
    ? normalizeToLocalDate(todayVisit.date) 
    : normalizeToLocalDate(new Date());

  // Use React Query hooks - MUST pass serviceDateStr so backend uses getReadingsForDayWithDateOnly
  const { data: locationData, isLoading, refetch } = useLocationReadings(locationId!, serviceDateStr, undefined, todayVisit?.id);
  
  // Log when locationData changes
  useEffect(() => {
    console.log("=== FRONTEND: locationData changed ===");
    console.log("locationData:", locationData);
    console.log("locationData?.trkEvents:", locationData?.trkEvents);
    console.log("locationData?.trkEvents count:", locationData?.trkEvents?.length || 0);
    if (locationData?.trkEvents && locationData.trkEvents.length > 0) {
      locationData.trkEvents.forEach((e: any, idx: number) => {
        console.log(`TRK Event ${idx}:`, {
          id: e.id,
          trkId: e.trkId,
          locationVisitId: e.locationVisitId,
          cassette1Load: e.cassette1Load,
          cassette2Load: e.cassette2Load,
          cassette3Load: e.cassette3Load,
          cassette4Load: e.cassette4Load,
          balanceAtDeparture: e.balanceAtDeparture
        });
      });
    }
  }, [locationData]);
  const { data: goaEventsData, isLoading: goaEventsLoading } = useGoaEvents(
    locationId!, 
    serviceDateStr, 
    true, // Only active GOAs for the form
    todayVisit?.id // Pass locationVisitId to ensure we only get events for this specific visit
  );

  // Process the data when it changes
  useEffect(() => {
    if (locationData) {
      let goaData = locationData.goas.map((x: any) => {
        return {
          ...x,
          serviceable: true
        }
      });
      goaData = orderBy(goaData, 'position', 'asc');

      console.log("=== locationData updated ===");
      console.log("locationData.trkEvents:", locationData.trkEvents);
      console.log("locationData.trkEvents count:", locationData.trkEvents?.length || 0);
      console.log("locationData.trkEvents details:", locationData.trkEvents?.map((e: any) => ({ 
        id: e.id, 
        trkId: e.trkId, 
        locationVisitId: e.locationVisitId, 
        date: e.date,
        cassette1Load: e.cassette1Load 
      })));

      setDynamicData({
        goas: goaData,
        trks: locationData.trks,
        atms: locationData.atms,
        ctms: locationData.ctms,
        goaEvents: locationData.goaEvents,
        trkEvents: locationData.trkEvents || [],
        atmEvents: locationData.atmEvents || [],
        ctmEvents: locationData.ctmEvents || []
      });
    }
  }, [locationData]);

  const handleDataRefresh = async () => {
    console.log("=== handleDataRefresh ===");
    console.log("locationId:", locationId);
    console.log("serviceDateStr:", serviceDateStr);
    console.log("todayVisit?.id:", todayVisit?.id);
    
    // Invalidate location-readings query with the exact key structure
    // Query key is: ['location-readings', locationId, validDate, onlyActive, locationVisitId]
    queryClient.invalidateQueries({ 
      queryKey: ['location-readings', locationId, serviceDateStr, undefined, todayVisit?.id] 
    });
    // Also invalidate without locationVisitId in case it's called differently
    queryClient.invalidateQueries({ 
      queryKey: ['location-readings', locationId, serviceDateStr] 
    });
    // Invalidate all location-readings for this location to be safe
    queryClient.invalidateQueries({ 
      predicate: (query) => query.queryKey[0] === 'location-readings' && query.queryKey[1] === locationId
    });
    
    // Also refresh the goaEvents data
    if (goaEventsData) {
      // Force a refetch of the goaEvents queries (with and without locationVisitId)
      queryClient.invalidateQueries({ queryKey: ['goa-events', 'current-day', locationId, serviceDateStr, true, todayVisit?.id] });
      queryClient.invalidateQueries({ queryKey: ['goa-events', 'current-day', locationId, serviceDateStr, true] });
      queryClient.invalidateQueries({ queryKey: ['goa-events', 'previous', locationId, serviceDateStr, true] });
    }
    
    // Force a manual refetch and wait for it to complete
    const result = await refetch();
    console.log("Refetch result:", result);
    console.log("Refetched data.trkEvents:", result.data?.trkEvents);
    return result;
  };

  const MAX_VISIBLE_ITEMS = 8; // Show up to 8 items before scrolling

  const [showGoaDetailModal, setShowGoaDetailModal] = useState(false);

  const prepareGoaData = (data: { goas: IGoa[], events: IGoaEvent[] }) => {
    console.log("=== prepareGoaData DEBUG ===");
    console.log("serviceDateStr:", serviceDateStr);
    console.log("todayVisit?.date:", todayVisit?.date);
    console.log("goaEventsData:", goaEventsData);
    console.log("currentEvents:", goaEventsData?.currentEvents);
    console.log("previousEvents:", goaEventsData?.previousEvents);
    
    // Filter out inactive GOAs (only show active ones)
    const activeGoas = data.goas.filter((goa: IGoa) => goa.active !== false);
    
    const goaData = activeGoas.map((goa: IGoa) => {
      // Get current day event from the new hook data
      const currentDayEvent = goaEventsData?.currentEvents?.find((event: any) => event.goaId === goa.id) || null;
      
      // Get previous event from the new hook data
      const previousEvent = goaEventsData?.previousEvents?.[goa.id as keyof typeof goaEventsData.previousEvents] || null;

      console.log(`GOA ${goa.assetId}: currentDayEvent:`, currentDayEvent);
      console.log(`GOA ${goa.assetId}: previousEvent:`, previousEvent);

      return {
        id: goa.id,
        assetId: goa.assetId,
        position: goa.position,
        hardIn: currentDayEvent?.hardIn || 0,
        hardOut: currentDayEvent?.hardOut || 0,
        lastHardIn: previousEvent?.hardIn || 0,
        lastHardOut: previousEvent?.hardOut || 0,
        event: currentDayEvent?.event || GOA_EVENT_ENTRY_TYPE.CASH_PULL,
        serviceIssue: currentDayEvent?.serviceIssue || ""
      }
    });

    return goaData;
  }

  const prepareTrkData = (data: { trks: ITrk[], events: ITrkEvent[] }) => {
    console.log("=== prepareTrkData DEBUG ===");
    console.log("TRKs count:", data.trks.length);
    console.log("TRK Events count:", data.events.length);
    console.log("TRK Events:", data.events.map(e => ({ 
      id: e.id, 
      trkId: e.trkId, 
      locationVisitId: e.locationVisitId, 
      cassette1Load: e.cassette1Load,
      cassette2Load: e.cassette2Load,
      cassette3Load: e.cassette3Load,
      cassette4Load: e.cassette4Load,
      balanceAtDeparture: e.balanceAtDeparture
    })));
    
    const trkData = data.trks.map((trk: ITrk) => {
      // Simple: Find the event for this TRK by trkId
      // Since we query by locationVisitId, all events are for the current visit
      const event = data.events.find((e: ITrkEvent) => e.trkId === trk.id);
      
      console.log(`TRK ${trk.assetId} (id: ${trk.id}):`, event ? {
        eventId: event.id,
        cassette1Load: event.cassette1Load,
        cassette2Load: event.cassette2Load,
        cassette3Load: event.cassette3Load,
        cassette4Load: event.cassette4Load,
        balanceAtDeparture: event.balanceAtDeparture
      } : 'NO EVENT FOUND');

      return {
        id: trk.id,
        assetId: trk.assetId,
        trkId: trk.id,
        locationId: locationId ? Number(locationId) : undefined,
        cassette1Load: event?.cassette1Load || 0,
        cassette2Load: event?.cassette2Load || 0,
        cassette3Load: event?.cassette3Load || 0,
        cassette4Load: event?.cassette4Load || 0,
        lastCassette1Load: 0, // TODO: Get from previous visit if needed
        lastCassette2Load: 0,
        lastCassette3Load: 0,
        lastCassette4Load: 0,
        balanceAtDeparture: event?.balanceAtDeparture || 0,
        serviceable: true,
        serviceIssue: '' // TRK events don't have serviceIssue field
      };
    });

    console.log("=== prepareTrkData RESULT ===");
    console.log("Prepared TRK data:", trkData.map(t => ({
      assetId: t.assetId,
      trkId: t.trkId,
      cassette1Load: t.cassette1Load,
      cassette2Load: t.cassette2Load,
      cassette3Load: t.cassette3Load,
      cassette4Load: t.cassette4Load,
      balanceAtDeparture: t.balanceAtDeparture
    })));
    return trkData;
  }


  const prepareAtmData = (data: { atms: IAtm[], events: IAtmEvent[] }) => {
    const atmData = data.atms.map((atm: IAtm) => {
      const atmEvents = data.events.filter((event: IAtmEvent) => event.atmId === atm.id);
      
      // Use serviceDateStr for date comparison (the date the user is working on)
      const targetDateStr = serviceDateStr;

      const todayEvent = atmEvents.find((event: IAtmEvent) => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        const eventDateStr = eventDate.toISOString().split('T')[0];
        return eventDateStr === targetDateStr;
      });

      let lastEvent = null;
      const targetDate = new Date(targetDateStr);
      targetDate.setHours(0, 0, 0, 0);
      const pastEvents = atmEvents.filter((event: IAtmEvent) => {
        if (!event.date) return false;
        const eventDate = new Date(event.date);
        return eventDate < targetDate;
      });

      if (pastEvents.length > 0) {
        lastEvent = pastEvents[pastEvents.length - 1];
      }

      return {
        id: atm.id,
        assetId: atm.assetId,
        lastLoad: lastEvent?.load || 0,
        lastBalanceAtDeparture: lastEvent?.balanceAtDeparture || 0,
        load: Number(todayEvent?.load || 0),
        balanceAtDeparture: Number(todayEvent?.balanceAtDeparture || 0),
        status: todayEvent?.status || null,
        event: 'Machine Fill',
        startAmount: todayEvent?.load || 0,
        lastStartAmount: lastEvent?.load || 0,
        cassette1Load: todayEvent?.cassette1Load || 0,
        lastCassette1Load: lastEvent?.cassette1Load || 0,
        cassette2Load: todayEvent?.cassette2Load || 0,
        lastCassette2Load: lastEvent?.cassette2Load || 0,
        cassette3Load: todayEvent?.cassette3Load || 0,
        lastCassette3Load: lastEvent?.cassette3Load || 0,
        cassette4Load: todayEvent?.cassette4Load || 0,
        lastCassette4Load: lastEvent?.cassette4Load || 0
      };
    });

    return atmData;
  }

  const renderAtmEventData = (data: { atms: IAtm[], events: IAtmEvent[] }) => {
    const preparedData = prepareAtmData(data);
    
    return (
      <CardContent sx={{ flex: 1, overflowY: data.atms.length > MAX_VISIBLE_ITEMS ? 'auto' : 'hidden', padding: '8px' }}>
        <ListItem key={'trks123'} sx={{ padding: '4px' }}>
          <ListItemText
            primary={<b>Asset Id</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Start Amount</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C1</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C2</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C3</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C4</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Balance at Departure</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
        </ListItem>
        {
          preparedData.map((atmEvent) => (
            <ListItem key={atmEvent.assetId} sx={{ padding: '4px' }}>
              <ListItemText
                primary={atmEvent.assetId}
                sx={{ textAlign: 'left', fontSize: '0.85rem' }}
              />
              <LastValueListItemText
                currentValue={atmEvent.startAmount}
                lastValue={atmEvent.lastStartAmount}
              />
              <LastValueListItemText
                currentValue={atmEvent.cassette1Load}
                lastValue={atmEvent.lastCassette1Load}
              />
              <LastValueListItemText
                currentValue={atmEvent.cassette2Load}
                lastValue={atmEvent.lastCassette2Load}
              />
              <LastValueListItemText
                currentValue={atmEvent.cassette3Load}
                lastValue={atmEvent.lastCassette3Load}
              />
              <LastValueListItemText
                currentValue={atmEvent.cassette4Load}
                lastValue={atmEvent.lastCassette4Load}
              />
              <LastValueListItemText
                currentValue={atmEvent.balanceAtDeparture}
                lastValue={atmEvent.lastBalanceAtDeparture}
              />
            </ListItem>
          ))
        }
      </CardContent>
    );
  }

  const renderTrkEventData = (data: { trks: ITrk[], events: ITrkEvent[] }) => {
    const preparedData = prepareTrkData(data);
    
    return (
      <CardContent sx={{ flex: 1, overflowY: data.trks.length > MAX_VISIBLE_ITEMS ? 'auto' : 'hidden', padding: '8px' }}>
        <ListItem key={'trks123'} sx={{ padding: '4px' }}>
          <ListItemText
            primary={<b>C1</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C2</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C3</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>C4</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Total value</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Balance Departure</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
        </ListItem>
        {
          preparedData.map((trkEvent) => (
            <ListItem key={trkEvent.assetId} sx={{ padding: '4px' }}>
              <LastValueListItemText
                currentValue={trkEvent.cassette1Load}
                lastValue={trkEvent.lastCassette1Load}
              />
              <LastValueListItemText
                currentValue={trkEvent.cassette2Load}
                lastValue={trkEvent.lastCassette2Load}
              />
              <LastValueListItemText
                currentValue={trkEvent.cassette3Load}
                lastValue={trkEvent.lastCassette3Load}
              />
              <LastValueListItemText
                currentValue={trkEvent.cassette4Load}
                lastValue={trkEvent.lastCassette4Load}
              />
              <ListItemText
                primary={trkEvent.cassette1Load + trkEvent.cassette2Load + trkEvent.cassette3Load + trkEvent.cassette4Load}
                sx={{ textAlign: 'left', fontSize: '0.85rem' }}
              />
              <LastValueListItemText
                currentValue={trkEvent.balanceAtDeparture}
                lastValue={trkEvent.balanceAtDeparture}
              />
            </ListItem>
          ))
        }
      </CardContent>
    );
  }

  const renderGoaEventDetailData = (data: { goas: IGoa[], events: IGoaEvent[] }) => {
    const preparedData = prepareGoaData(data);
    return (
      <CardContent sx={{ flex: 1, overflowY: data.goas.length > MAX_VISIBLE_ITEMS ? 'auto' : 'hidden', padding: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Asset Id</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>#</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Hard In</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Hard In</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Hard Out</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Hard Out</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Money In</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Money Out</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Net</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Commission</th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {preparedData.map((goa) => {
              const moneyIn = goa.hardIn - goa.lastHardIn;
              const moneyOut = goa.hardOut - goa.lastHardOut;
              const net = moneyIn - moneyOut;
              const commission = net * ((location?.commissionPercentage || 0) / 100);
              const totalNet = net - commission;

              if (!goa.hardIn || !goa.hardOut) {
                return (
                  <tr key={goa.assetId}>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.assetId}</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.position}</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                    <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>-</td>
                  </tr>
                );
              }

              return (
                <tr key={goa.assetId}>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.assetId}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.position}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.hardIn.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd', color: 'rgba(0, 0, 0, 0.4)' }}>{goa.lastHardIn.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{goa.hardOut.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd', color: 'rgba(0, 0, 0, 0.4)' }}>{goa.lastHardOut.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{moneyIn.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{moneyOut.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{net.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{commission.toFixed(2)}</td>
                  <td style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>{totalNet.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    );
  };

  const renderGoaEventData = (data: { goas: IGoa[], events: IGoaEvent[] }) => {

    console.log("goa data", data)
    const preparedData = prepareGoaData(data);
    
    return (
      <CardContent sx={{ flex: 1, overflowY: data.goas.length > MAX_VISIBLE_ITEMS ? 'auto' : 'hidden', padding: '8px' }}>
        <ListItem key={'abc123'} sx={{ padding: '4px' }}>
          <ListItemText
            primary={<b>Asset Id</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Hard In</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
          <ListItemText
            primary={<b>Hard Out</b>}
            sx={{ textAlign: 'left', fontSize: '0.85rem' }}
          />
        </ListItem>
        {preparedData.map((goa) => (
          <ListItem key={goa.assetId} sx={{ padding: '4px' }}>
            <ListItemText
              primary={goa.assetId}
              sx={{ textAlign: 'left', fontSize: '0.85rem' }}
            />
            <LastValueListItemText
              currentValue={goa.hardIn}
              lastValue={goa.lastHardIn}
            />
            <LastValueListItemText
              currentValue={goa.hardOut}
              lastValue={goa.lastHardOut}
            />
          </ListItem>
        ))}
      </CardContent>
    );

  }

  const renderStandardAsset = (data: any) => {

    return (
      <CardContent sx={{ flex: 1, overflowY: data.length > MAX_VISIBLE_ITEMS ? 'auto' : 'hidden', padding: '8px' }}>
        {data.length > 0 ? (
          <List dense sx={{ maxHeight: '180px', overflowY: 'auto' }}>
            {data.map((item: any, index: any) => (
              <ListItem key={index} sx={{ padding: '4px' }}>
                <ListItemText
                  primary={item.assetId}
                  sx={{ textAlign: 'center', fontSize: '0.85rem' }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" align="center" color="textSecondary">
            No assets available
          </Typography>
        )}
      </CardContent>
    )
  }

  const hasAssets = (title: string, data: any) => {
    if (!data) return false;
    
    switch (title) {
      case 'GOAs': return data.goas && data.goas.length > 0;
      case 'TRKs': return data.trks && data.trks.length > 0;
      case 'ATMs': return data.atms && data.atms.length > 0;
      case 'CTMs': return Array.isArray(data) && data.length > 0;
      default: return false;
    }
  };

  const getVisibleAssetCount = () => {
    if (!dynamicData) return 0;
    
    let count = 0;
    if (hasAssets("GOAs", { goas: dynamicData.goas, events: dynamicData.goaEvents })) count++;
    if (hasAssets("TRKs", { trks: dynamicData.trks, events: dynamicData.trkEvents })) count++;
    if (hasAssets("ATMs", { atms: dynamicData.atms, events: dynamicData.atmEvents })) count++;
    if (hasAssets("CTMs", dynamicData.ctms)) count++;
    
    return count;
  };

  const getGridSize = () => {
    const visibleCount = getVisibleAssetCount();
    
    if (visibleCount === 0) return { xs: 12, sm: 12, md: 12 };
    if (visibleCount === 1) return { xs: 12, sm: 12, md: 12 };
    if (visibleCount === 2) return { xs: 12, sm: 6, md: 6 };
    if (visibleCount === 3) return { xs: 12, sm: 6, md: 6, lg: 4 };
    if (visibleCount === 4) return { xs: 12, sm: 6, md: 6 };
    
    return { xs: 12, sm: 6, md: 6 };
  };

  const renderAssetCard = (title: string, data: any, detailRender: (data: any) => JSX.Element, actionGreen?: () => void, actionRed?: () => void) => {
    const getStatusForType = (type: string) => {
      if (!todayVisit) return DEFAULT_STATUS;
      
      switch (type) {
        case 'GOAs': return todayVisit.goaStatus;
        case 'TRKs': return todayVisit.trkStatus;
        case 'CTMs': return todayVisit.ctmStatus;
        case 'ATMs': return todayVisit.atmStatus;
        default: {
          console.log("default status",type, DEFAULT_STATUS);
          return DEFAULT_STATUS
        }
      }
    };

    const status = getStatusForType(title);
    const statusText = status?.replace(/_/g, ' ') || DEFAULT_STATUS.replace(/_/g, ' ');

    return (
      <Card variant="outlined" sx={{ minWidth: 275, height: 500, display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {title}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getStatusIcon(status)}
                <Typography variant="caption" color="textSecondary">
                  {statusText}
                </Typography>
              </Box>
              {title === 'GOAs' && hasAssets(title, data) && <>
                <IconButton 
                  size="small" 
                  onClick={() => setShowGoaDetailModal(!showGoaDetailModal)}
                  sx={{ ml: 1 }}
                  title="View Detailed Report"
                >
                  {<ViewListIcon />}
                </IconButton>
              </>}
            </Box>
          }
          sx={{
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            padding: '8px',
            fontSize: '0.9rem'
          }}
        />

        {detailRender(data)}

        {/* Buttons at the bottom of the card */}
        {hasAssets(title, data) && actionGreen && (
          <Box sx={{ display: 'flex', justifyContent: 'right', padding: '8px' }}>
            <Button variant="contained" size="small" onClick={actionGreen}>Service</Button>
          </Box>
        )}
      </Card>
    );
  };

  const openUpsertGoaModal = () => {
    if (!dynamicData) return;

    console.log("=== openUpsertGoaModal DEBUG ===");
    console.log("goaEventsLoading:", goaEventsLoading);
    console.log("goaEventsData:", goaEventsData);

    // Wait for goaEventsData to be loaded
    if (goaEventsLoading || !goaEventsData) {
      console.log("Waiting for goaEventsData to load...");
      return;
    }

    const preparedData = prepareGoaData({ goas: dynamicData.goas, events: dynamicData.goaEvents });
    console.log("preparedData:", preparedData);
    
    const goaValuesMapped = preparedData.map((goa) => ({
      hardIn: goa.hardIn,
      hardOut: goa.hardOut,
      lastHardIn: goa.lastHardIn,
      lastHardOut: goa.lastHardOut,
      locationId: locationId ? parseInt(locationId) : undefined,
      assetId: goa.assetId,
      goaId: goa.id,
      position: goa.position,
      event: goa.event as GOA_EVENT_ENTRY_TYPE,
      serviceable: (goa.event !== GOA_EVENT_ENTRY_TYPE.UNSERVICEABLE),
      serviceIssue: goa.serviceIssue
    }));

    console.log("goaValuesMapped:", goaValuesMapped);
    setGoaValues(goaValuesMapped);
    setOpenGoaModal(true);
  };

  const openUpsertTrkModel = () => {
    if (!dynamicData) return;

    console.log("=== FRONTEND: openUpsertTrkModel ===");
    console.log("dynamicData.trks count:", dynamicData.trks?.length || 0);
    console.log("dynamicData.trkEvents count:", dynamicData.trkEvents?.length || 0);
    console.log("todayVisit?.id:", todayVisit?.id);
    console.log("dynamicData.trkEvents:", dynamicData.trkEvents);

    const preparedData = prepareTrkData({ trks: dynamicData.trks, events: dynamicData.trkEvents || [] });
    console.log("=== FRONTEND: Prepared TRK data for modal ===");
    preparedData.forEach((trk, idx) => {
      console.log(`Prepared TRK ${idx}:`, {
        id: trk.id,
        trkId: trk.trkId,
        assetId: trk.assetId,
        cassette1Load: trk.cassette1Load,
        cassette2Load: trk.cassette2Load,
        cassette3Load: trk.cassette3Load,
        cassette4Load: trk.cassette4Load,
        balanceAtDeparture: trk.balanceAtDeparture
      });
    });
    
    setTrkValues(preparedData as any);
    setOpenTrkModal(true);
  };

  const openUpsertAtmModal = () => {
    if (!dynamicData) return;

    // Always prepare fresh data from current dynamicData to ensure we have the latest events
    const preparedData = prepareAtmData({ atms: dynamicData.atms, events: dynamicData.atmEvents || [] });

    setAtmValues(preparedData as any);
    setOpenAtmModal(true);
  };

  return (
    <>
      <StandardModal 
        title="GOA Detail"
        open={showGoaDetailModal}
        handleClose={() => setShowGoaDetailModal(false)}
        action={() => {
          console.log("action")
        }}
        maxWidth="xl"
        children={dynamicData ? renderGoaEventDetailData({ goas: dynamicData.goas, events: dynamicData.goaEvents }) : <div>Loading...</div>}
      />
      <LocationVisitGoaForm
        goaValues={goaValues}
        setGoaValues={setGoaValues}
        handleDataRefresh={handleDataRefresh}
        openGoaModal={openGoaModal}
        setOpenGoaModal={setOpenGoaModal}
      />
      <LocationVisitTrkForm
        trkValues={trkValues}
        setTrkValues={setTrkValues}
        handleDataRefresh={handleDataRefresh}
        openTrkModal={openTrkModal}
        setOpenTrkModal={setOpenTrkModal}
      />
      <LocationVisitAtmForm
        atmValues={atmValues}
        setAtmValues={setAtmValues}
        handleDataRefresh={handleDataRefresh}
        openAtmModal={openAtmModal}
        setOpenAtmModal={setOpenAtmModal}
      />

      <TitleBar
        breadcrumbs={breadcrumbs}
        title={`${location?.name || 'Location'} Service`}
        rightContent={

          <>
          
            <Button 
              variant="contained" 
              onClick={() => {
                if (locationId) {
                  navigate(`/app/location-service/${locationId}/reports`);
                }
              }}
              disabled={!todayVisit || (todayVisit.percentComplete ?? 0) < 100}
              title={!todayVisit || (todayVisit.percentComplete ?? 0) < 100 ? "Complete all service tasks before generating reports" : "View Reports"}
            >Reports</Button>
          </>
        }
      />
      <LocationVisitOverview />

      <Grid container spacing={2} sx={{ padding: '16px 0' }}>
        {dynamicData && (
          <>
            {hasAssets("GOAs", { goas: dynamicData.goas, events: dynamicData.goaEvents }) && (
              <Grid size={getGridSize()}>
                {renderAssetCard("GOAs", { goas: dynamicData.goas, events: dynamicData.goaEvents }, renderGoaEventData, openUpsertGoaModal)}
              </Grid>
            )}
            {hasAssets("TRKs", { trks: dynamicData.trks, events: dynamicData.trkEvents }) && (
              <Grid size={getGridSize()}>
                {renderAssetCard("TRKs", { trks: dynamicData.trks, events: dynamicData.trkEvents }, renderTrkEventData, () => openUpsertTrkModel())}
              </Grid>
            )}
            {hasAssets("ATMs", { atms: dynamicData.atms, events: dynamicData.atmEvents }) && (
              <Grid size={getGridSize()}>
                {renderAssetCard("ATMs", { atms: dynamicData.atms, events: dynamicData.atmEvents }, renderAtmEventData, () => openUpsertAtmModal())}
              </Grid>
            )}
            {hasAssets("CTMs", dynamicData.ctms) && (
              <Grid size={getGridSize()}>
                {renderAssetCard("CTMs", dynamicData.ctms, renderStandardAsset)}
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <OutageTable type="service" assetId={Number(locationId)} outages={outages} onSubmitted={handleDataRefresh} />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
              <LocationRejectsTable locationId={locationId!} dateFilter={today}/>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

export default LocationServicePage;
